'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import ListCell from '@/components/ui/ListCell';
import { Divider } from '@/components/ui/Divider';
import { IconButton } from '@/components/ui/IconButton';
import { IconAiSparkles, IconSearch, IconChevronLeft, IconRewindClock } from '@repo/svgs';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAiSearchController } from './hooks/useAiSearchController';
import { useRotatingPlaceholder } from './hooks/useRotatingPlaceholder';
import { RotatingPlaceholder } from './components/RotatingPlaceholder';

export interface AISearchBarMobileProps {
  className?: string;
  placeholder?: string;
  /** Controlled open state for the full-page overlay (e.g. opened from the header mini bar). */
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  /**
   * Render the idle collapsed pill. Defaults to `true` (home in-page usage). Set
   * `false` when a separate header mini bar is the trigger (e.g. non-home mweb
   * pages) so only the full-page overlay is rendered.
   */
  showTrigger?: boolean | undefined;
}

/**
 * The AI-powered home search bar (mobile web), faithful to the Figma `mweb` spec.
 *
 * Idle: a compact pill (sparkles + placeholder + circular search button) that sits
 * in the home top bar. Tapping it opens a full-screen search overlay: a back
 * button, a multi-line expanding input card with the circular search button pinned
 * bottom-right, and a keyboard-aware suggestions/recents list. Unlike desktop there
 * is no "Search {count} listings" button on mobile.
 *
 * Gated by `useAiSearchStatus` upstream (see `HomeSearchMobile`), so this only
 * mounts when the AI bar is enabled.
 */
export function AISearchBarMobile({
  className,
  placeholder = 'What are you looking for?',
  open: controlledOpen,
  onOpenChange,
  showTrigger = true,
}: AISearchBarMobileProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (onOpenChange) onOpenChange(next);
      else setInternalOpen(next);
    },
    [onOpenChange],
  );
  const [mounted, setMounted] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const pillTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const analytics = useAnalytics();

  const {
    query,
    setQuery,
    isSubmitting,
    suggestions,
    recents,
    refreshRecents,
    submitTyped,
    goToUrl,
  } = useAiSearchController(true);

  const rotatingPlaceholder = useRotatingPlaceholder(placeholder, suggestions, query.length === 0);

  // Tapping the collapsed pill = tapping the input on mobile.
  const openSearch = React.useCallback(() => {
    analytics.track('header_listings_ai_text');
    setOpen(true);
  }, [analytics]);

  React.useEffect(() => setMounted(true), []);

  // While the overlay is open: refresh recents, lock body scroll, focus the input.
  React.useEffect(() => {
    if (!open) return;
    refreshRecents();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(focusTimer);
    };
  }, [open, refreshRecents]);

  const autosize = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, []);

  React.useEffect(() => {
    if (open) autosize();
  }, [open, query, autosize]);

  // Keep the collapsed pill's (read-only) textarea sized to its content so a long
  // preserved query wraps onto multiple lines instead of truncating.
  const autosizePill = React.useCallback(() => {
    const el = pillTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }, []);

  React.useEffect(() => {
    autosizePill();
  }, [query, autosizePill]);

  // Every submit route lands here (both search buttons and the keyboard's
  // Enter/Search key), so the event is tracked once here rather than per-route.
  const handleSubmit = React.useCallback(() => {
    // The buttons self-disable while submitting; the keyboard key needs the same
    // guard so a second press can't re-navigate or double-count the event.
    if (isSubmitting) return;
    analytics.track('header_listings_ai_search');
    if (query.trim().length > 0) {
      submitTyped();
    } else {
      // Empty query → verified-listings default (not persisted filters) (#17, #18i).
      goToUrl('/listings/status:verified');
    }
  }, [isSubmitting, analytics, query, submitTyped, goToUrl]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // The keyboard's Enter/Search key always searches — never inserts a
      // newline (#18k). (The field is a textarea only so long queries wrap.)
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  // Recents (rewind icon) take priority; only fall back to the static suggestions
  // (magnifier icon) when there is no recent history (#18f). The list is shown only
  // while the query is empty — matching the Figma (typing hides the list).
  const rows =
    recents.length > 0
      ? recents.map(r => ({
          key: `recent-${r.query}`,
          title: r.query,
          icon: <IconRewindClock className="w-6 h-6 icon-secondary" />,
          onClick: () => {
            analytics.track('header_listings_ai_recent');
            goToUrl(r.url, r.query);
          },
        }))
      : suggestions.map(s => ({
          key: `suggestion-${s.url || s.text}`,
          title: s.text,
          icon: <IconSearch className="w-6 h-6 icon-secondary" />,
          onClick: () => {
            analytics.track('header_listings_ai_suggestion');
            goToUrl(s.url, s.text);
          },
        }));
  const showList = query.trim().length === 0 && rows.length > 0;

  const CircleSearchButton = (
    <button
      type="button"
      aria-label="Search"
      onClick={handleSubmit}
      disabled={isSubmitting}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-inverted disabled:opacity-60"
    >
      {isSubmitting ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
      ) : (
        <IconSearch className="w-4 h-4" />
      )}
    </button>
  );

  return (
    <>
      {/* Idle collapsed pill. Tapping the text opens the full-screen overlay; the
          circular button submits directly (empty → verified default) (#17, #18g).
          Skipped when a separate header mini bar is the trigger. */}
      {showTrigger ? (
        <div className={cn('px-6', className)}>
          <div
            className={cn(
              // 8px inset (py-2 / pr-2); sparkle 16px from the left (pl-4). The sparkle
              // stays vertically centered with the text as the query wraps (#8).
              'flex w-full items-start gap-2 rounded-full border border-secondary bg-primary py-2 pl-4 pr-2 shadow',
              'min-h-[52px]',
            )}
          >
            <IconAiSparkles className="w-5 h-5 icon-tertiary shrink-0 self-center" />
            <div className="relative min-w-0 flex-1">
              <textarea
                ref={pillTextareaRef}
                value={query}
                readOnly
                rows={1}
                onClick={openSearch}
                aria-label="Search apartments with AI"
                className="block w-full cursor-pointer resize-none bg-transparent py-1.5 leading-6 text-body-2 text-primary outline-none"
              />
              {/* `pointer-events-none`, so a tap on the placeholder still opens the overlay. */}
              <RotatingPlaceholder
                text={rotatingPlaceholder}
                hidden={query.length > 0}
                className="py-1.5 leading-6 text-body-2"
              />
            </div>
            <button
              type="button"
              aria-label="Search"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex h-9 w-9 shrink-0 items-center justify-center self-end rounded-full bg-accent text-inverted disabled:opacity-60"
            >
              <IconSearch className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : null}

      {/* Full-screen search overlay */}
      {mounted && open
        ? createPortal(
            <div className="fixed inset-0 z-[100] flex flex-col bg-primary">
              {/* Back — icon-button component, 16px from the top; no shadow (#18a, #18b). */}
              <div className="flex items-center px-2 pt-4">
                <IconButton
                  aria-label="Back"
                  type="transparent"
                  icon={IconChevronLeft}
                  onClick={() => setOpen(false)}
                  className="!bg-transparent !shadow-none"
                />
              </div>

              {/* Expanding input card — 8px inset so the button sits 8px from the
                  border (#18d); ~12px above from the back button (#18c). */}
              <div className="px-4 pt-3">
                <div className="rounded-3xl border border-secondary bg-primary p-2 shadow">
                  <div className="flex gap-2 pl-2 pt-1">
                    <IconAiSparkles className="mt-1 w-5 h-5 icon-tertiary shrink-0" />
                    <div className="relative min-w-0 flex-1">
                      <textarea
                        ref={textareaRef}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onInput={autosize}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        autoComplete="off"
                        enterKeyHint="search"
                        aria-label="Search apartments with AI"
                        className="block w-full resize-none bg-transparent py-1 text-body-2 text-primary outline-none"
                      />
                      <RotatingPlaceholder
                        text={rotatingPlaceholder}
                        hidden={query.length > 0}
                        className="py-1 text-body-2"
                      />
                    </div>
                  </div>
                  <div className="mt-2 flex justify-end">{CircleSearchButton}</div>
                </div>
              </div>

              {/* Suggestions / recents — 16px below the search bar (#18h), with a
                  divider line between rows (#18e). */}
              {showList ? (
                <div className="mt-4 flex flex-1 flex-col overflow-y-auto px-4 gap-1">
                  {rows.map((row, idx) => (
                    <React.Fragment key={row.key}>
                      <ListCell
                        title={row.title}
                        leadingIcon={row.icon}
                        trailing="chevron"
                        onClick={row.onClick}
                      />
                      {idx !== rows.length - 1 ? (
                        <div className="px-2">
                          <Divider variant="tertiary" />
                        </div>
                      ) : null}
                    </React.Fragment>
                  ))}
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export default AISearchBarMobile;
